/**
 * ✅ **출시 준비 상태** — 아직 안 채운 자리를 한 곳에 모은다.
 *
 * ## 왜 필요한가 (2026-08-21)
 * 아직 안 채운 자리가 **여섯 화면에 흩어져** 있다 — 굿즈 스토어 주소, AR 「영상으로 보기」,
 * AR 포스터, 행사, 랠리 신청 폼, 문의 폼, 체험공간 링크. 운영자가 "뭘 더 해야 하나"를
 * 알 방법이 없어서, 지금은 개발자가 표로 알려주고 있다. 그건 오래 못 간다.
 *
 * ## 여기서 하는 일과 안 하는 일
 * 이 파일은 **판정만** 한다 — 무엇이 비었는지, 어느 화면에서 채우는지. 어떻게 생겼는지는 화면 몫이다.
 * 관리자 화면과 (나중에) 배포 점검이 **같은 판정**을 쓰게 하려고 shared 에 둔다.
 * 화면마다 다시 세면 반드시 갈린다 — 이 저장소가 반복해서 당한 사고다.
 *
 * ## 판정 규칙
 * "비었다"는 **아이 화면에서 아쉬운 것**만 센다. 안 쓰는 기능까지 빨갛게 만들면 목록이
 * 늑대소년이 되고, 운영자는 곧 전부 무시한다. 그래서 심각도를 나눈다:
 *  - `must`  : 지금 아이가 손해를 본다(눌러도 못 가는 버튼, 못 보는 영상)
 *  - `nice`  : 있으면 좋지만 없어도 동작한다
 */
import { OWNER_DUE } from "./ownerTodo";

export type ReadySeverity = "must" | "nice";

export interface ReadyItem {
  id: string;
  /** 어느 관리자 화면에서 채우나(ADMIN_SCREENS 의 id) */
  screen: string;
  severity: ReadySeverity;
  /** 무엇이 비었나 — 한 줄 */
  label: string;
  /** 안 채우면 아이 화면이 어떻게 되나 */
  effect: string;
  /** 남은 개수(0이면 이 항목은 목록에 안 나온다) */
  count: number;
  /** 약속한 기한(YYYY-MM-DD, `OWNER_DUE`) — 있으면 화면이 「D-3」·「9일 지남」을 붙인다 */
  due?: string;
}

export interface ReadyInput {
  /** 스토어 주소가 하나도 없는 상품 수 / 전체 상품 수 */
  goodsMissingStore: number;
  goodsTotal: number;
  /** 「영상으로 보기」가 빈 AR 카드 수 / 포스터가 빈 카드 수 */
  arMissingVideo: number;
  /**
   * 🔴 **아이 화면 목록에 아예 안 뜨는 AR 카드.**
   * 「영상이 비었다」와 다르다 — 그건 *보이는* 카드의 폴백 얘기고, 이건 카드 자체가 없는 것이다.
   * (운영 확인 2026-08-30: 경주 카드 6장 중 5장이 이랬는데 목록엔 「영상이 비었다」만 떴다.)
   */
  arHidden: number;
  arMissingPoster: number;
  /** 아이 화면에 보이도록 켜 둔 행사 수 */
  visibleEvents: number;
  /**
   * 🎥 **화상이 아직 안 열리는 행사 수** — 옛 Jitsi 방 이름만 남아 있는 경우.
   *
   * 회의 주소 칸을 만들어 두고도 **접힘 안에 있어** 운영자가 못 봤다 —
   * 운영 게시본 실측(2026-09-03): 「북토크」가 그 상태로 돌고 있었다.
   *
   * 🔴 예전엔 「방 이름이 찍히기 쉬운가」도 세고 있었는데, 그 방을 이젠 **아무도 안 열므로**
   * 고칠 수 없는 거짓 경고가 됐다 — 같이 내렸다.
   */
  eventJitsiOnly: number;
  /** 어느 행사인지 — 개수만으로는 못 찾는다. */
  eventJitsiOnlyWhere?: string[];
  /**
   * 🧪 **아이 화면에 「테스트」처럼 보이는 행사가 떠 있나.**
   *
   * 2026-08-20 에 데모 행사가 「지금 열리고 있어요」로 운영에 떠 있었고, 그때 `visible`
   * 스위치를 만들었다. 그런데 **스위치는 켜 두는 것을 막지 못한다** — 2026-09-04 에도
   * 「북토크 테스트」가 켜진 채였다(웨일온을 확인하려고 켠 것이라 잘못이 아니라,
   * **끄는 것을 잊기 쉬운 것**이다).
   */
  eventTestVisible?: number;
  /** 어느 행사인지. */
  eventTestVisibleWhere?: string[];
  /** 랠리 상품 신청 폼 주소가 들어 있나 */
  hasRallyForm: boolean;
  /** 랠리를 쓰고 있나(지점이 있으면 쓰는 것으로 본다) */
  rallyPoints: number;
  /** 마을 소식 아래 문의 폼 주소가 들어 있나 */
  hasContactUrl: boolean;
  /**
   * 📢 **아이 화면 [마을 이야기]에 걸린 소식 수.**
   *
   * 운영 실측(2026-09-05): `GET /api/community` 가 `notices: []` 를 준다 —
   * 보호자가 [마을 이야기]를 눌러도 소식 칸이 **통째로 비어** 있다. 에러도 안 나고
   * 관리자에서도 「0건」이라 안 적혀 있어서, 아무도 빈 줄 모른 채 서 있었다.
   *
   * 🔴 **못 읽었을 때는 0 을 넣지 말 것.** 「없다」와 「못 물어봤다」가 같은 숫자가 되면
   * 이 목록이 없는 문제를 만들어 낸다(이 저장소가 반복해서 당한 자리).
   * 못 읽었으면 `undefined` 를 넣는다 — 그러면 이 항목은 안 뜬다.
   */
  noticeCount?: number;
  /** 체험공간에 놓였는데 주소가 비어 있는 링크·가게 오브젝트 수 */
  worldLinksMissing: number;
  /**
   * 🔗 **어느 구역의 무엇인지** — 개수만으로는 못 찾는다(`worldStuckWhere` 와 같은 이유).
   */
  worldLinksWhere?: string[];
  /** 🐱 아직 ✅ 안 한 AI 문답 수 — 쌓여 있으면 아무도 모른 채 잠들어 있다 */
  qaPending: number;
  /** 문답을 켜 뒀는데(1단계 이상) 아이에게 나갈 답이 하나도 없는 친구 수 */
  agentsEmpty: number;
  /**
   * 볼 자료를 골라 뒀는데 **그 자료가 비어 있는** 친구 수.
   *
   * 묶음을 만들고 이름까지 붙였는데 글을 안 붙여넣은 경우다 — 관리자에서는 체크가 들어가
   * 있어서 다 된 것처럼 보이고, AI 초안은 「고른 자료가 비어 있어요」로 거절당한다.
   */
  agentsEmptyPacks: number;
  /**
   * 📚📚 **고르기 화면에 같은 이름이 나란히 선 책 수.**
   *
   * 책을 들이는 길이 둘이라(관리자 업로드 · 저장소) 같은 원고를 반씩 넣으면 두 권이 된다.
   * 하나는 미완성이고 아이는 어느 쪽을 눌러야 할지 모른다. 목록이 길어질 뿐 에러는 안 난다.
   */
  dupBookTitles: number;
  /**
   * 🎙️ **자막은 있는데 읽어주기 소리가 없는 쪽 수.**
   *
   * 아이 화면에서는 그 쪽만 🔊 버튼이 안 뜬다 — 에러도 빈 칸도 없어서, 읽어 주다가
   * 어느 쪽에서 소리가 안 나는지는 **끝까지 넘겨 봐야** 안다. 글자 없는 면은 세지 않는다.
   */
  dubMissing: number;
  /**
   * 🪄 **AR 재료는 올렸는데 카메라가 알아볼 그림(타깃)이 없는 쪽 수.**
   *
   * 그 쪽은 아이 화면에 **[AR로 보기] 버튼이 아예 안 뜬다** — 영상·그림을 올려도 잠들어 있다.
   * 관리자에서는 다 채운 것처럼 보이고, 어디에도 그 말이 없다(2026-08-23 신고).
   */
  arNoTarget: number;
  /**
   * 🪄 **마법을 반만 만든 쪽**(docs/build/35). 층 그림은 올렸는데 켜 주는 자리가 없거나,
   * 누르는 자리가 없는 층을 가리키는 등. 판정은 `arInteractIssues` 한 곳이라
   * 관리자 편집기와 **같은 말**을 한다.
   *
   * 🔴 못 읽었으면 세지 않는다(`undefined`) — 「없다」와 「못 물어봤다」는 다른 말이다.
   */
  magicHalfDone?: number;
  /**
   * 👻 **책에 없는 쪽에 걸린 AR 타깃 수.**
   *
   * 책은 살아 있는데 그 **쪽만** 없는 경우다 — 쪽을 지우거나 쪽 id 를 바꾸면 AR 타깃은
   * 그대로 남는다. `bookOrphanAssets`(책 자체가 없는 것)와도, `arNoTarget`(타깃이 없는
   * 것)과도 다른 세 번째 경우라 **지금까지 아무 데도 안 세고 있었다.**
   *
   * 🔴 아이 화면에 그대로 나간다. 쪽 번호는 책 차례에서 오는데(2026-09-03 수정) 그 쪽이
   * 책에 없으면 번호를 지어내지 않으므로, 목록에 **「p07」** 같은 개발자 이름이 뜨고
   * 체험 화면은 **「p07를 비춰보세요」** 라고 말한다. 영유아·보호자용 한글 화면에서.
   * 게다가 그 타깃은 어느 쪽 그림으로도 안 맞으니 카메라는 영영 아무것도 못 알아본다.
   */
  arPageGone: number;
  /**
   * 📚 **어느 책에도 안 붙은 그림·소리 수.**
   *
   * 관리자에서 책을 만들다 말면(쪽은 올렸는데 책을 저장 안 했거나, 나중에 책만 지웠거나)
   * 올린 파일은 그대로 남는데 **아이 화면에는 그 책이 아예 없다.**
   * 관리자에도 아무 말이 없어서, 운영자는 「분명히 올렸는데」만 겪는다
   * (2026-08-28 실제로 31개가 그 상태였다 — 표지·본문 10쪽·읽어주기 20개).
   *
   * AR 의 `arNoTarget` 과 같은 종류다: **재료는 있는데 그걸 쓰는 자리가 없다.**
   */
  bookOrphanAssets: number;
  /** 🛍️ 어느 상품 것도 아닌 사진 수 — 상품을 지워도 사진은 남는다(책과 같은 종류). */
  goodsOrphanImages: number;
  /**
   * 🧱 **벽에 갇힌 문·친구·링크·가게 수.**
   *
   * 화면에는 멀쩡히 보이고 에러도 없다 — 아이는 다가가려다 못 가고 그냥 지나친다.
   * 2026-09-02 게시본 실측: 마을의 이벤트 광장 **문**과 도서관의 「나무 위 도서관」이
   * 벽에 반쯤 묻혀 있었다. 문은 걸어 들어가면 열리는 것인데(24px) 가장 가까운 열린
   * 자리가 25px 였다. 그때까지 **어디에도 그 말이 없었다.**
   */
  worldStuck: number;
  /**
   * 🧱 **어느 구역의 무엇이 갇혔는지** — 개수만으로는 운영자가 못 찾는다.
   *
   * 🔴 지금까지 「벽에 갇힌 문·친구·링크 **2개**」라고만 했다. 구역이 다섯이고 오브젝트는
   * 수십 개다 — 운영자는 다섯 화면을 열어 하나씩 눈으로 찾아야 한다. 그래서 **안 고친다.**
   * 실제로 이 항목은 며칠째 그대로다(2026-09-03 게시본 실측: 마을 `portal_event` ·
   * 도서관 `read_tree`).
   *
   * 「마을 · portal_event」처럼 **바로 찾아갈 수 있는 말**로 준다. 없으면 빈 배열.
   */
  worldStuckWhere?: string[];
  /**
   * 🧑‍💼 **`pnpm check:owner` 와 같은 사람 일**(2026-10-09). 예전엔 명령줄에서만 보여서
   * 운영자는 개발자에게 물어야 알았다. 판정은 shared `ownerTodo` 한 곳.
   * 🔴 전부 **못 물어봤으면 `undefined`** — 그러면 항목이 안 뜬다(「없다」와 「못 물어봤다」는 다르다).
   */
  /** 영유아 모드가 게시됐나 */
  kidsModePublished?: boolean;
  /** 보호자 함께 읽기 문구 — 채운 장면·전체 장면 */
  guardianDone?: number;
  guardianTotal?: number;
  /** 첫 화면 연령 딱지 수(3이면 끝) · 홍보 문구가 첫 화면에 있나 */
  landingBands?: number;
  landingTagline?: boolean;
  /** 게시본 마을에 이벤트 광장 옆문이 있나 */
  sideDoor?: boolean;
}

/**
 * 비어 있는 것만 돌려준다(count 0 은 제외). 심각한 것부터.
 *
 * 랠리 신청 폼은 **랠리를 실제로 쓸 때만** 센다 — 지점이 하나도 없으면 안 하는 행사다.
 * 행사는 "0개"가 정상일 수 있어서 `nice` 다(상시 운영만 할 수도 있다).
 */
/**
 * 어디 무엇인지를 한 줄에 붙인다. **너무 길면 아무도 안 읽는다** — 셋까지만 적고 나머지는 세준다.
 * 목록이 비어 있으면(못 물어봤거나 아직 안 넣은 경우) **아무것도 안 붙인다** — 지어내지 않는다.
 */
function whereSuffix(where: readonly string[] | undefined): string {
  if (!where || where.length === 0) return "";
  const head = where.slice(0, 3).join(" · ");
  const rest = where.length > 3 ? ` 외 ${where.length - 3}개` : "";
  return ` — ${head}${rest}`;
}

export function releaseChecks(i: ReadyInput): ReadyItem[] {
  const items: ReadyItem[] = [
    // ── 🧑‍💼 사람 일 — `pnpm check:owner` 와 같은 판정(shared ownerTodo). 기한이 코앞이라 맨 앞 ──
    {
      id: "kids-mode",
      screen: "uifeat",
      severity: "must",
      label: "🧸 영유아 모드가 아직 게시 안 됐어요",
      effect: "첫 화면이 아직 일곱 칸 그대로예요. 켜기만 하고 [게시]를 안 누르면 아이 화면은 안 바뀌어요.",
      count: i.kidsModePublished === false ? 1 : 0,
    },
    {
      id: "landing-age",
      screen: "home",
      severity: "must",
      label: `첫 화면 연령 딱지 ${i.landingBands ?? 0}/3 · 홍보 문구 「그림책이 살아나는 놀이터」 ${i.landingTagline ? "있음" : "없음"}`,
      // 무엇이 빠졌는지에 맞춰 말한다 — 딱지를 다 넣었는데 「몇 살 것인지가 안 보여요」라고 하면 없는 일을 쫓는다
      effect:
        ((i.landingBands ?? 0) < 3
          ? "몇 살 것인지가 첫 화면에 안 보여요(0~3세·2세~·4~7세 세 칸). "
          : "연령 딱지는 됐어요. 홍보 문구만 큰 제목·말풍선 중 한 칸에 넣으면 돼요. ") +
        "바꾸기 전에 첫 화면을 먼저 캡처해 두세요 — 전후 비교가 증빙이에요.",
      count: i.landingBands === undefined ? 0 : i.landingBands < 3 || !i.landingTagline ? 1 : 0,
    },
    {
      id: "side-door",
      screen: "zones",
      severity: "must",
      label: "그림책 마을에 이벤트 광장 옆문이 없어요",
      effect:
        "아이가 무대 쪽으로 곧장 끌면 기둥에 막혀 위로 미끄러져요(관찰 테스트 「마을 걷기」). " +
        "[🗺️ 체험공간 배치] → 그림책 마을 → 맨 위 「저장소 지도에만 있는 물건」의 [배치에 더하기] → [웹에 올리기].",
      count: i.sideDoor === false ? 1 : 0,
    },
    {
      id: "guardian",
      screen: "magic",
      severity: "nice",
      label: `보호자 함께 읽기 문구 ${i.guardianDone ?? 0}/${i.guardianTotal ?? 0}장면`,
      effect:
        "마법 장면에서 보호자에게 「이렇게 물어봐 주세요」가 안 떠요. " +
        "[🪄 마법 점검] 아래 「보호자 문구 한눈에」에서 한 표로 채울 수 있어요(빈 칸에 초안 넣기도 돼요).",
      count:
        i.guardianTotal === undefined || i.guardianDone === undefined ? 0 : i.guardianTotal - i.guardianDone,
    },
    {
      id: "goods-store",
      screen: "goods",
      severity: "must",
      label: `살 수 있는 곳이 없는 굿즈 ${i.goodsMissingStore}개`,
      effect: "아이 화면에서 「스토어 준비 중이에요」로 보이고 살 수 없어요.",
      count: i.goodsMissingStore,
    },
    {
      // 🔴 이게 「영상이 비었다」보다 **먼저**다 — 카드가 목록에 없으면 영상 얘기는 뒤의 일이다
      id: "ar-hidden",
      screen: "arcard",
      severity: "must",
      label: `아이 화면에 아예 안 뜨는 AR 카드 ${i.arHidden}장`,
      effect: "목록에 안 나와요. 카드 그림(.mind)이나 영상 중 하나는 있어야 떠요.",
      count: i.arHidden,
    },
    {
      id: "ar-video",
      screen: "arcard",
      severity: "must",
      label: `「영상으로 보기」가 빈 AR 카드 ${i.arMissingVideo}장`,
      effect:
        "카메라가 안 될 때(구형 폰·거부·어두운 곳) 아이는 안내 글만 보고 끝나요. " +
        "(카드 자체가 목록에 없는 것은 위 줄을 보세요.)",
      count: i.arMissingVideo,
    },
    {
      id: "goods-orphan",
      screen: "goods",
      severity: "nice",
      label: `어느 상품 것도 아닌 사진 ${i.goodsOrphanImages}장`,
      effect: "지운 상품의 사진이 남아 있어요. 같은 이름으로 다시 만들면 그대로 붙어요.",
      count: i.goodsOrphanImages,
    },
    {
      id: "book-orphan",
      screen: "book",
      severity: "must",
      label: `어느 책에도 안 붙은 그림·소리 ${i.bookOrphanAssets}개`,
      effect: "올린 파일은 남아 있지만 그 책이 아이 화면에 없어요. 책을 다시 만들거나 파일을 지워 주세요.",
      count: i.bookOrphanAssets,
    },
    {
      id: "world-link",
      screen: "zones",
      severity: "must",
      // 🔴 **어디 무엇인지**를 붙인다 — 개수만 주면 다섯 화면을 눈으로 뒤져야 해서 안 고친다
      label: `주소가 빈 체험공간 링크·가게 ${i.worldLinksMissing}개${whereSuffix(i.worldLinksWhere)}`,
      /**
       * 🔴 **설명이 옛날 화면을 말하고 있었다.** 예전엔 정말 「눌러도 아무 일이 없었다」.
       * 그건 고쳤다 — 지금은 단추가 아예 안 눌리고 「스토어 준비 중이에요」라고 말한다
       * (운영 확인: 굿즈 마켓 여섯 가게 모두 못 누르는 상태로 뜬다). 코드는 좋아졌는데
       * 설명만 옛날에 남아 있으면, 운영자가 **없는 고장을 쫓는다.**
       */
      effect: "아이가 상품을 열면 「준비 중이에요」만 보이고 살 수가 없어요.",
      count: i.worldLinksMissing,
    },
    {
      id: "world-stuck",
      screen: "zones",
      severity: "must",
      // 🔴 **어디 무엇인지**를 붙인다 — 개수만 주면 다섯 화면을 눈으로 뒤져야 해서 안 고친다
      label: `벽에 갇힌 문·친구·링크 ${i.worldStuck}개${whereSuffix(i.worldStuckWhere)}`,
      effect:
        "화면에는 보이는데 아이가 다가갈 수가 없어요. 문이면 걸어서 못 들어가요(눌러야 열려요). " +
        "[🗺️ 체험공간 배치]에서 한 칸 옮겨 다시 게시해 주세요.",
      count: i.worldStuck,
    },
    {
      id: "event-jitsi-only",
      screen: "rally",
      /**
       * 🔴 「덜 된 것」이 아니라 **행사 날 아무것도 안 열린다** — 사람들이 모인 뒤에 안다.
       *
       * 🔴 이 문구는 **두 번** 틀렸다. 같은 이유로.
       *
       * ① Jitsi 폴백을 없애기 전에 쓴 「통화가 5분 뒤 끊긴다」를 그대로 뒀더니, 폴백을
       *    없앤 뒤에는 사실이 아니게 됐다(이제 아예 안 열린다).
       * ② 그래서 「이젠 안 쓰기로 했고」로 고쳤는데, 그 다음 날 **Jitsi 를 다시 고를 수
       *    있게** 만들었다 — 안 쓰는 게 아니라 **쓰는 방법이 달라진 것**이다.
       *    그렇게 말하면 운영자는 멀쩡한 방을 버리고 새로 만든다.
       *
       * 코드를 바꾸면 **화면이 하는 말도 같이** 봐야 한다. 그리고 여기와
       * `JITSI_ONLY_SAY` 는 **같은 상황**을 말하므로 서로 어긋나면 안 된다 —
       * `releaseJitsiSay.test.ts` 가 둘을 견준다.
       */
      severity: "must",
      label: `화상이 아직 안 열리는 행사 ${i.eventJitsiOnly}개${whereSuffix(i.eventJitsiOnlyWhere)}`,
      effect:
        "옛 방식(방 이름만)으로 남아 있어요. 그건 화면에 끼워 넣는 방식이라 5분 뒤 끊겨서 " +
        "지금은 아예 안 열립니다. [🎁 행사·랠리]에서 그 행사의 「회의 주소」 칸에 방 주소를 " +
        "넣어 주세요 — 웨일온·구글밋·줌은 물론 같은 Jitsi 방도 주소로 넣으면 새 창으로 " +
        "열려서 안 끊겨요. 그 자리에 [↪ 이 방을 「회의 주소」로 옮기기] 단추가 있어요.",
      count: i.eventJitsiOnly,
    },
    {
      id: "event-test-visible",
      screen: "rally",
      /**
       * 🔴 **막지 않는다 — 물어볼 뿐이다.** 진짜로 「테스트」가 들어간 제목의 행사를
       * 열 수도 있다(공개 리허설 등). 그래서 「반드시 고칠 것」이 아니라
       * **「이거 켜 둔 거 맞나요?」**다. 잔소리가 되면 목록 전체를 안 믿게 된다.
       */
      severity: "nice",
      label: `아이 화면에 보이는 「테스트」 행사 ${i.eventTestVisible ?? 0}개${whereSuffix(
        i.eventTestVisibleWhere,
      )}`,
      effect:
        "제목에 테스트·데모 같은 말이 들어 있는데 아이 화면에 떠 있어요. 아이와 보호자는 " +
        "그 제목을 그대로 읽어요 — 참가 신청을 하거나, 그 시각에 들어와서 아무도 없는 방을 봅니다. " +
        "확인용으로 켜 두신 것이면 [🎁 행사·랠리]에서 그 행사의 보이기를 꺼 주세요. " +
        "진짜 행사라면 그대로 두셔도 됩니다.",
      count: i.eventTestVisible ?? 0,
    },
    {
      id: "rally-form",
      screen: "rally",
      severity: "must",
      // 랠리를 안 쓰면(지점 0) 아예 목록에 안 올린다
      label: "랠리 상품 신청 폼 주소",
      effect: "6곳을 다 찍은 아이가 선물을 신청할 곳이 없어요.",
      count: i.rallyPoints > 0 && !i.hasRallyForm ? 1 : 0,
    },
    {
      id: "ar-poster",
      screen: "arcard",
      severity: "nice",
      label: `목록 그림(포스터)이 빈 AR 카드 ${i.arMissingPoster}장`,
      effect: "AR 고르는 화면에서 그림 없이 글자만 보여요(글 못 읽는 아이는 못 골라요).",
      count: i.arMissingPoster,
    },
    {
      id: "event-none",
      screen: "rally",
      severity: "nice",
      label: "아이 화면에 보이는 행사가 없어요",
      effect: "메인 배너와 [마을 이야기]에 행사 칸이 안 보여요. 행사를 안 열면 정상이에요.",
      count: i.visibleEvents === 0 ? 1 : 0,
    },
    {
      /**
       * 🔴 검수 대기는 **관리자 안쪽 화면에만** 보인다. 초안을 만들어 두고 잊으면
       * 그대로 잠든다 — 만든 사람은 「했다」고 기억하는데 아이 화면엔 아무것도 안 나온다.
       */
      id: "qa-pending",
      screen: "chars",
      severity: "nice",
      label: `아직 ✅ 안 한 AI 문답 ${i.qaPending}개`,
      effect: "✅ 를 누르기 전에는 아이에게 안 나가요. 만들어 두고 잊기 쉬워요.",
      count: i.qaPending,
    },
    {
      id: "ar-no-target",
      screen: "book",
      // 🔴 올려 둔 것이 아이에게 하나도 안 닿는다 — 「덜 된 것」이 아니라 안 보이는 것이다.
      severity: "must",
      label: `AR 재료만 있고 카메라가 못 알아보는 쪽 ${i.arNoTarget}개`,
      effect: "올린 영상·그림이 아이 화면에 아예 안 나와요. [AR로 보기] 버튼도 안 떠요.",
      count: i.arNoTarget,
    },
    {
      id: "magic-half",
      screen: "book",
      // 🔴 아이가 눌러도 아무 일이 안 나거나, 올린 그림이 영영 안 보인다 — 「덜 된 것」이 아니다.
      severity: "must",
      label: `마법을 반만 만든 쪽 ${i.magicHalfDone ?? 0}개`,
      effect:
        "아이가 눌러도 아무 일이 안 나거나, 올린 그림이 영영 안 보여요. " +
        "[📖 전자책] → 그 쪽 → 🪄 마법 꾸미기에서 무엇이 빠졌는지 알려 줘요.",
      count: i.magicHalfDone ?? 0,
    },
    {
      id: "ar-page-gone",
      screen: "book",
      // 🔴 고장이다 — 아이 화면에 개발자 이름(`p07`)이 그대로 나가고, 카메라는 영영 못 알아본다.
      severity: "must",
      label: `책에서 사라진 쪽에 걸린 AR ${i.arPageGone}개`,
      effect: "AR 목록에 「p07」 같은 이름이 그대로 떠요. 비춰도 카메라가 영영 못 알아봐요.",
      count: i.arPageGone,
    },
    {
      id: "dub-missing",
      screen: "dub",
      // 「덜 된 것」이다 — 소리가 없어도 책은 읽힌다. 다만 아무 데도 안 적히면 그대로 잊힌다.
      severity: "nice",
      label: `읽어주기가 없는 쪽 ${i.dubMissing}개`,
      effect: "그 쪽에서는 🔊 버튼이 안 떠요. 글씨를 못 읽는 아이는 혼자 못 넘어가요.",
      count: i.dubMissing,
    },
    {
      id: "book-dup",
      screen: "book",
      // 🔴 「덜 된 것」이 아니라 고장이다 — 아이가 미완성인 쪽을 열면 이야기가 중간에 끊긴다.
      severity: "must",
      label: `이름이 같은 책 ${i.dupBookTitles}쌍`,
      effect: "같은 책이 두 권 나란히 보여요. 아이가 덜 만든 쪽을 열 수 있어요.",
      count: i.dupBookTitles,
    },
    {
      id: "agent-packs-empty",
      screen: "aikb",
      severity: "nice",
      label: `고른 자료가 비어 있는 친구 ${i.agentsEmptyPacks}명`,
      effect: "묶음은 골랐는데 글이 안 담겨 있어요. AI 초안도 못 만들어요.",
      count: i.agentsEmptyPacks,
    },
    {
      id: "agent-empty",
      screen: "chars",
      severity: "must",
      label: `말은 켰는데 할 말이 없는 친구 ${i.agentsEmpty}명`,
      effect: "아이가 눌러도 물어볼 것이 하나도 안 보여요.",
      count: i.agentsEmpty,
    },
    {
      /**
       * 🔴 못 읽었으면(`undefined`) 세지 않는다 — 「없다」와 「못 물어봤다」는 다른 말이다.
       * 0 으로 뭉개면 소식이 멀쩡히 있는데도 빨갛게 뜬다.
       */
      id: "notice-none",
      screen: "notice",
      severity: "nice",
      label: "마을 소식이 한 건도 없어요",
      effect: "[마을 이야기] 화면의 소식 칸이 통째로 비어 보여요. 한 줄만 써도 채워져요.",
      count: i.noticeCount === 0 ? 1 : 0,
    },
    {
      id: "contact",
      screen: "notice",
      severity: "nice",
      label: "문의·제보 폼 주소",
      effect: "[마을 이야기] 아래 문의 버튼이 안 보여요.",
      count: i.hasContactUrl ? 0 : 1,
    },
  ];
  for (const it of items) {
    const due = OWNER_DUE[it.id];
    if (due) it.due = due;
  }
  const rank = (s: ReadySeverity) => (s === "must" ? 0 : 1);
  // 🔴 기한으로 다시 줄 세우지 않는다 — 「안 뜨는 카드」가 「영상이 빈 카드」(기한 있음)보다 먼저여야 한다.
  //    차례는 위 배열이 정하고, 기한은 딱지로만 보인다.
  return items.filter((x) => x.count > 0).sort((a, b) => rank(a.severity) - rank(b.severity));
}

/** 목록 맨 위에 쓸 한 줄 요약. 다 채웠으면 그렇게 말해 준다. */
export function readySummary(items: ReadyItem[]): string {
  const must = items.filter((x) => x.severity === "must").length;
  if (items.length === 0) return "다 채웠어요! 아이 화면에서 비어 있는 곳이 없어요. 🎉";
  if (must === 0) return `꼭 채워야 할 건 없어요. 있으면 더 좋은 것 ${items.length}가지가 남았어요.`;
  return `아이가 지금 손해를 보는 것 ${must}가지가 있어요.`;
}
